import Vimeo, { ProgressEvent } from '@u-wave/react-vimeo';

import { store, useAppSelector } from '../stores/AppStore';
import {
  getPopupVideoWidth,
  thinkerMentorWelcomeVideoUrl,
  thinkerMentorWelcomeVideoMinimumWatchPercentage,
} from '../constants';
import {
  selectWelcomeVideoWatchedPct,
  setWelcomeVideoWatchedPct,
  selectShowWelcomeVideoPopup,
  setShowWelcomeVideoPopup,
} from '../features/global/globalSlice';
import Modal from 'react-modal';
import { XIcon } from '@heroicons/react/outline';
import { useState } from 'react';
import { debounce } from 'lodash';
import { useUpdateUserMetricsSettingsMutation } from '../services/apiSlice';

const createVideoProgressHandler = (
  updateMetrics: ReturnType<typeof useUpdateUserMetricsSettingsMutation>[0],
) => {
  // Initialize with current percentage from store to avoid re-saving already saved progress
  const currentState = store.getState();
  let lastSavedPercentage =
    currentState.global.metrics.welcomeVideoWatchedPct || 0;

  return debounce(
    (event: ProgressEvent) => {
      const percentage = Math.round(event.percent * 100);
      store.dispatch(setWelcomeVideoWatchedPct(percentage));

      // Only update backend at meaningful thresholds to reduce API calls
      const thresholds = [10, 25, 50, 75, 90, 100];
      const shouldUpdate = thresholds.some(
        (threshold) =>
          percentage >= threshold && lastSavedPercentage < threshold,
      );

      if (shouldUpdate || percentage === 100) {
        lastSavedPercentage = percentage;

        // Update the backend with the new percentage
        updateMetrics({
          metrics: { welcomeVideoWatchedPct: percentage },
          settings: { show: {} }, // No settings update needed here
        })
          .unwrap()
          .catch((error) => {
            console.error('Failed to update video watch progress:', error);
            // Don't show error to user for background updates
            // The local state is already updated, so the user experience is not affected
          });
      }
    },
    1000,
    { trailing: true },
  );
};

const WelcomeVideoPopup = () => {
  const welcomeVideoWatchedPct = useAppSelector(selectWelcomeVideoWatchedPct);
  const didWatchWelcomeVideo =
    (welcomeVideoWatchedPct ?? 0) >=
    thinkerMentorWelcomeVideoMinimumWatchPercentage;
  const showWelcomeVideoPopup = useAppSelector(selectShowWelcomeVideoPopup);
  const [isPopupTemporarilyHidden, setPopupTemporarilyHidden] =
    useState<boolean>(false);
  const [hasVideoStarted, setHasVideoStarted] = useState<boolean>(false);
  const [updateUserMetricsSettings] = useUpdateUserMetricsSettingsMutation();
  const isDevelopmentOrTestEnvironment =
    process.env.REACT_APP_NODE_ENV === 'test' ||
    process.env.REACT_APP_NODE_ENV === 'development';

  const onVideoProgress = createVideoProgressHandler(updateUserMetricsSettings);

  const videoModalStyles: Modal.Styles = {
    content: {
      zIndex: 55,
      overflow: 'hidden',
      borderRadius: '12px',
      top: '50%',
      left: '50%',
      right: 'auto',
      bottom: 'auto',
      transform: 'translate(-50%, -50%)',
    },
    overlay: {
      zIndex: 50,
    },
  };

  const dismissPopup = () => {
    // If the video has been fully watched (100%), allow closing without confirmation
    if (welcomeVideoWatchedPct >= 100) {
      setPopupTemporarilyHidden(true);
      // Update the Redux state to hide the popup
      store.dispatch(setShowWelcomeVideoPopup(false));

      // Persist the popup dismissal to the backend
      updateUserMetricsSettings({
        metrics: {}, // No metrics update needed here
        settings: { show: { welcomeVideoPopup: false } },
      })
        .unwrap()
        .catch((error) => {
          console.error('Failed to update popup dismissal:', error);
          // The popup is already hidden locally, so user experience is not affected
          // Could show a toast here if needed, but it might be disruptive
        });
      return;
    }

    // Otherwise, show the confirmation dialog
    const confirmed = window.confirm(
      'To ensure you have a great experience, we encourage all users to watch this short video. If this is not a convenient time, you may dismiss it now and it will appear next time for your review.',
    );
    if (confirmed) {
      setPopupTemporarilyHidden(true);
      // Update the Redux state to hide the popup
      store.dispatch(setShowWelcomeVideoPopup(false));

      // Persist the popup dismissal to the backend
      updateUserMetricsSettings({
        metrics: {}, // No metrics update needed here
        settings: { show: { welcomeVideoPopup: false } },
      })
        .unwrap()
        .catch((error) => {
          console.error('Failed to update popup dismissal:', error);
          // The popup is already hidden locally, so user experience is not affected
          // Could show a toast here if needed, but it might be disruptive
        });
    }
  };

  const onVideoPlay = () => {
    setHasVideoStarted(true);
  };

  return (
    <Modal
      isOpen={
        (!didWatchWelcomeVideo || showWelcomeVideoPopup || hasVideoStarted) &&
        !isPopupTemporarilyHidden &&
        !isDevelopmentOrTestEnvironment
      }
      onRequestClose={dismissPopup}
      style={videoModalStyles}
    >
      <div className="">
        <div
          className="absolute grid justify-items-end w-full top-2 pr-6 cursor-pointer"
          onClick={dismissPopup}
        >
          <XIcon className="h-5 w-5" />
        </div>
        <div className="grid justify-items-center items-center mt-3">
          <Vimeo
            video={thinkerMentorWelcomeVideoUrl}
            width={getPopupVideoWidth()}
            controls={true}
            speed={true}
            // onReady={onReady}
            autoplay={true}
            onPlay={onVideoPlay}
            onProgress={onVideoProgress}
          />
        </div>
      </div>
    </Modal>
  );
};

export default WelcomeVideoPopup;
